/**
 * Administrator «Ціни РДН» (iteration 2): tomorrow's day-ahead prices pasted
 * from the market operator's site, ₴/MWh, one value per line. As typed — the
 * hour count or the error («Рядок 3: «abc» — не число»); «Зберегти». Nothing
 * else on the screen (owner, 20.10.2026): these are the prices Energy Map
 * loads later and writes over, no matching, charts or statuses.
 */
import { Box } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import type { AdminDamPricesBody, AdminDamPricesResponse, AdminDamPricesTargetResponse } from 'shared';

import { useLocale } from '../../i18n/locale-context.js';
import { ApiError, api } from '../../lib/api-client.js';
import { fill, formatCalendarDate } from './admin-format.js';
import {
  ADMIN_TOKENS,
  AdminPage,
  PillButton,
  QueryState,
  Toast,
  cardSx,
  errorReason,
  h2Sx,
  subSx,
  useIsAdmin,
} from './admin-ui.js';
import { damPasteMessage, damPricesIntro, parseDamPaste } from './dam-paste.js';

const T = ADMIN_TOKENS;

export function OperatorDamPricesPage() {
  const { t, locale } = useLocale();
  const isAdmin = useIsAdmin();
  const target = useQuery({
    queryKey: ['admin', 'dam-prices'],
    queryFn: () => api.get<AdminDamPricesTargetResponse>('/api/admin/dam-prices'),
    enabled: isAdmin,
  });
  const [text, setText] = useState('');
  const [saved, setSaved] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const day = target.data;
  const date = day ? formatCalendarDate(day.date) : '';
  const parsed = useMemo(() => (day ? parseDamPaste(text, day.hours) : null), [text, day]);
  const values = parsed && 'values' in parsed ? parsed.values : null;
  const error = parsed && 'error' in parsed ? damPasteMessage(parsed.error, t, locale, date) : saveError;

  // Tomorrow moved on while the page was open (midnight, or a 422 from the
  // server): what was pasted is the old day's — cleared, not saved for the new one.
  const dayDate = day?.date;
  const shownDate = useRef(dayDate);
  useEffect(() => {
    if (!dayDate) return;
    if (shownDate.current && shownDate.current !== dayDate) {
      setText('');
      setSaved(null);
      setSaveError(fill(t('operator.prices.err.date_changed'), { date: formatCalendarDate(dayDate) }));
    }
    shownDate.current = dayDate;
  }, [dayDate, t]);

  const save = useMutation({
    mutationFn: (body: AdminDamPricesBody) => api.put<AdminDamPricesResponse>('/api/admin/dam-prices', body),
    onSuccess: (res) => {
      setSaved(fill(t('operator.prices.saved'), { date: formatCalendarDate(res.date) }));
      window.scrollTo(0, 0);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 422) {
        const expected = err.details?.expected;
        // Tomorrow moved on: the refetch brings the new day, the effect above clears the text.
        if (err.code === 'validation_error' && typeof expected === 'string') {
          setSaveError(fill(t('operator.prices.err.date_changed'), { date: formatCalendarDate(expected) }));
          void target.refetch();
          return;
        }
        if (err.code === 'wrong_hour_count' && typeof expected === 'number') {
          setSaveError(
            damPasteMessage({ kind: 'count', got: Number(err.details?.got), expected }, t, locale, date),
          );
          void target.refetch();
          return;
        }
      }
      setSaveError(fill(t('operator.prices.err.save'), { reason: errorReason(err, t) }));
    },
  });

  const onSubmit = (ev: FormEvent) => {
    ev.preventDefault();
    if (!day || !values || save.isPending) return;
    setSaved(null);
    setSaveError(null);
    save.mutate({ market_id: day.market_id, date: day.date, hourly_local_per_mwh: values });
  };

  return (
    <AdminPage
      title={t('operator.prices.title')}
      toast={saved ? <Toast text={saved} onClose={() => setSaved(null)} /> : null}
    >
      {!day ? (
        <QueryState error={target.error} onRetry={() => target.refetch()} />
      ) : (
        <Box component="section" sx={cardSx}>
          <Box component="h2" sx={{ ...h2Sx, mb: '16px' }}>
            {fill(t('operator.prices.heading'), { date })}
          </Box>
          <Box component="p" sx={{ ...subSx, m: '0 0 14px', maxWidth: '72ch' }}>
            {damPricesIntro(day.hours, t, locale, date)}
          </Box>
          <form onSubmit={onSubmit} noValidate>
            <Box
              component="label"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                fontSize: 13,
                color: T.slate,
                minWidth: 0,
              }}
            >
              <Box component="span" sx={{ color: T.navy, fontWeight: 500, fontSize: 13.5 }}>
                {fill(t('operator.prices.label'), { date })}
              </Box>
              <Box
                component="textarea"
                value={text}
                onChange={(ev) => {
                  setText(ev.target.value);
                  setSaveError(null);
                }}
                spellCheck={false}
                placeholder={t('operator.prices.placeholder')}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'dam-paste-error' : values ? 'dam-paste-hours' : undefined}
                sx={{
                  font: 'inherit',
                  fontSize: 14.5,
                  lineHeight: 1.45,
                  color: T.navy,
                  fontVariantNumeric: 'tabular-nums',
                  border: `1px solid ${T.lineStrong}`,
                  borderRadius: '10px',
                  px: '12px',
                  py: '10px',
                  bgcolor: T.bg,
                  width: '100%',
                  boxSizing: 'border-box',
                  minHeight: 190,
                  resize: 'vertical',
                  '&:focus-visible': {
                    outline: `2px solid ${T.sky}`,
                    outlineOffset: '1px',
                    borderColor: T.sky,
                  },
                }}
              />
            </Box>
            <div aria-live="polite">
              {error ? (
                <Box
                  component="p"
                  id="dam-paste-error"
                  sx={{ color: T.errText, fontSize: 13, m: 0, mt: '10px' }}
                >
                  {error}
                </Box>
              ) : null}
              {values ? (
                <Box component="p" id="dam-paste-hours" sx={{ ...subSx, m: 0, mt: '10px' }}>
                  {fill(t('operator.prices.hours'), { n: values.length })}
                </Box>
              ) : null}
            </div>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', mt: '18px' }}>
              <PillButton type="submit" primary disabled={!values || save.isPending}>
                {fill(t('operator.prices.save'), { date })}
              </PillButton>
            </Box>
          </form>
        </Box>
      )}
    </AdminPage>
  );
}
